/**
 * HarvestStatusStrip · single-line summary of the Phase A·Step-3 data
 * harvest result for an approved vector. Three visual states:
 *
 *   • PENDING (no manifest yet)
 *       — appears between Accept-click and background task completion
 *       — neutral grey with a subtle pulse
 *   • OK
 *       — green strip with the local slice path + B2 confirmation
 *       — clicking "↻ re-harvest" re-runs the pipeline (useful when
 *         B2 was offline during the first attempt)
 *   • FAILED
 *       — rose strip with the recorded error
 *       — same re-harvest button doubles as a retry
 *
 * Always shows the canonical "✓ flows into Dashboard" line because the
 * vector's analytics inclusion is independent of the harvest's
 * persistence — the heatmap doesn't need the cut MP4, only the
 * field-XY endpoints.
 *
 * Extracted from PassVectorTriage.jsx in the Phase A · Step 5 refactor.
 */
import React from "react";
import { Database, Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

export function HarvestStatusStrip({ vector, busy, onRetrigger }) {
  const m = vector.harvest_manifest;
  const status = m?.status;
  const bytes = m?.slice?.video?.bytes;
  const localPath = m?.slice?.video?.local_path;
  const b2Status = m?.b2?.status;
  const error = m?.error;

  let tone, label, detail;
  if (!m) {
    tone = "text-muted-foreground";
    label = "Harvest pending";
    detail = busy ? "queueing background task…" : "background task scheduled";
  } else if (status === "ok") {
    tone = "text-emerald-300/80";
    const size = bytes ? `${(bytes / 1024 / 1024).toFixed(2)} MB slice` : "slice cached";
    const b2 = b2Status === "uploaded" ? "B2 backup uploaded" : (b2Status ? `B2 ${b2Status}` : "B2 pending");
    label = "Harvested";
    detail = `${size} · ${b2} · ${m.metadata?.lines || "?"} metadata lines`;
  } else {
    tone = "text-rose-300/80";
    label = "Harvest failed";
    detail = error || "see backend logs";
  }

  return (
    <div className="mt-1.5 flex items-center justify-between gap-2">
      <div className="font-mono text-[9px] uppercase tracking-widest text-emerald-300/70">
        ✓ flows into the Dashboard Heatmap + Pass-Stats
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <div
          data-testid={`harvest-status-${vector.id}`}
          className={`font-mono text-[9px] uppercase tracking-widest flex items-center gap-1.5 ${tone}`}
          title={localPath || ""}
        >
          <Database className="w-3 h-3" />
          {label} · <span className="normal-case tracking-normal">{detail}</span>
        </div>
        <Button
          data-testid={`harvest-retrigger-${vector.id}`}
          size="sm" variant="ghost" onClick={onRetrigger} disabled={busy}
          title="Re-run the data-harvest pipeline (cut slice + push to B2)"
          className="h-6 px-2 font-mono text-[9px] uppercase tracking-widest text-muted-foreground hover:text-primary hover:bg-accent"
        >
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : (
            <><RefreshCw className="w-3 h-3 mr-1" /> Re-harvest</>
          )}
        </Button>
      </div>
    </div>
  );
}
